import React, { useState, useEffect } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { getCookie } from "../utils/cookies";
import { userPermission } from "./oauthRoutes";
import UpdatePassword from "../components/user-management/current-user/UpdatePassword";
import NotFound from "../components/global/NotFound";
import { ROUTE_AUTH_APP_LIST, ADMIN_TYPES } from "../utils";
import SignIn from "../components/global/SignIn";
import Profile from "../components/user-management/current-user/Profile";

/* Application List */
import {
    AbbvieProAppList,
    AllerganProAppList,
    ApplicationList,
    GlobalConfig,
    UserManagerAppList,
} from "../components/application-list/";

/* Embed JS */
import EmbedJSList from "../components/embed-js/EmbedJSList";
import CreateEmbedJS from "../components/embed-js/CreateEmbedJS";

/* Activity Log */
import UserActivityLog from "../components/user-management/UserActivityLog";

/* Basic Login User List */
import BasicLoginUserList from "../components/user-management/basic-login/BasicLoginUserList";
import CreateBasicLoginUser from "../components/user-management/basic-login/CreateBasicLoginUser";
import AdminUserList from "../components/user-management/admin/AdminUserList";

/* Forms */
import FormList from "../components/form-data/FormList";
import FormInfo from "../components/form-data/FormInfo";
import CreateForm from "../components/form-data/CreateForm";
/* Group Speciality */
import CountrySpecialtyList from "../components/group-specialty/CountrySpecialtyList";
import CreateGroupSpeciality from "../components/group-specialty/CreateGroupSpeciality";
import GroupSpecialtyList from "../components/group-specialty/GroupSpecialtyList";
import CreateGroupSpecialityMap from "../components/group-specialty/CreateSpeciality";
/* Veeva ID */
import VeevaList from "../components/veeva-data/VeevaList";
import CreateVeeva from "../components/veeva-data/CreateVeeva";
/* Persona */
import CreatePersonaGroup from "../packages/personalization-framework/components/persona-group/create";
import PersonaAppList from "../packages/personalization-framework/components";
import PersonaGroup from "../packages/personalization-framework/components/persona-group";
import CreatePersonaAudience from "../packages/personalization-framework/components/persona-audience/create";
import PersonaAudience from "../packages/personalization-framework/components/persona-audience";

/* Basic Login Config */
import BasicLoginConfigList from "../components/basic-login/BasicLoginConfigList";
import CreateBasicLoginConfig from "../components/basic-login/CreateBasicLoginConfig";

/* LMBC */
import DermFinder from "../packages/derm-finder/component/";
import CreateDermFinder from "../packages/derm-finder/component/create";
import CreateAdmin from "../components/user-management/admin/CreateAdmin";

/* AbbVie ID */
import AbbvieIDConfig from "../components/application-list/AbbvieIDConfig";
import ConfiguredAppList from "../components/abbvieID/app-list/ConfiguredAppList";
import CreateAbbvieIDConfiguredApp from "../components/abbvieID/app-list/CreateIccConfigApp";
import IccConfigList from "../components/abbvieID/icc-config/IccConfigList";
import CreateIccConfig from "../components/abbvieID/icc-config/CreateIccConfig";
import VeevaConfigList from "../components/abbvieID/veeva-config/VeevaConfigList";
import CreateVeevaConfig from "../components/abbvieID/veeva-config/CreateVeevaConfig";
import TierCountryList from "../components/tier/TierCountryList";
import CreateTier from "../components/tier/CreateTier";
import CountryTierList from "../components/tier/CountryTierList";

const MainRoutes = () => {
    const [auth, setAuth] = useState({});
    const [loading, setLoading] = useState(true);
    const token = getCookie("piap_jwt_token");
    const user = useSelector((state) => state.users.currentUser);

    useEffect(() => {
        if (user) {
            setAuth(userPermission(user));
            setLoading(false);
        }
    }, [user]);

    return (
        <Routes>
            <Route path="/" element={<ApplicationList user={user} />} />
            <Route path="/profile" element={<Profile />} />
            <Route path="/update-password" element={<UpdatePassword />} />

            {auth.superAdmin && (
                <>
                    <Route path="/global-config" element={<GlobalConfig />} />
                    {/* Embed JS */}
                    <Route path="/global-config/embed-js" element={<EmbedJSList />} />
                    <Route path="/global-config/embed-js/create" element={<CreateEmbedJS />} />

                    {/* Basic Login */}
                    <Route path="/global-config/basic-login" element={<BasicLoginConfigList />} />
                    <Route
                        path="/global-config/basic-login/create"
                        element={<CreateBasicLoginConfig />}
                    />

                    {/* User List */}
                    <Route path="/user-list" element={<UserManagerAppList auth={auth} />} />

                    {/* User Manager Create URL */}
                    <Route
                        path="/user-list/user-manager/create"
                        element={<CreateAdmin userType={"user-manager"} />}
                    />

                    {/* Abbvie ID Configuration */}
                    <Route path="/global-config/abbvie-id" element={<AbbvieIDConfig />} />

                    <Route
                        path="/global-config/abbvie-id/app-list"
                        element={<ConfiguredAppList />}
                    />
                    <Route
                        path="/global-config/abbvie-id/app-list/create"
                        element={<CreateAbbvieIDConfiguredApp />}
                    />

                    <Route path="/global-config/abbvie-id/icc-config" element={<IccConfigList />} />

                    <Route
                        path="/global-config/abbvie-id/icc-config/create"
                        element={<CreateIccConfig />}
                    />

                    <Route
                        path="/global-config/abbvie-id/veeva-config"
                        element={<VeevaConfigList />}
                    />

                    <Route
                        path="/global-config/abbvie-id/veeva-config/create"
                        element={<CreateVeevaConfig />}
                    />
                </>
            )}

            {(auth.superAdmin || auth.siteAdmin) && (
                <Route
                    path={`${auth.superAdmin ? "/global-config" : ""}/activity-log`}
                    element={<UserActivityLog />}
                />
            )}

            {(auth.superAdmin || (auth.siteAdmin && auth.pro)) && (
                <>
                    {/* Abbvie Pro */}
                    <Route path="/abbvie-pro" element={<AbbvieProAppList auth={auth} />} />
                    <Route
                        path="/abbvie-pro/forms"
                        element={<FormList appName={ROUTE_AUTH_APP_LIST.ABBVIEPRO} />}
                    />
                    <Route
                        path="/abbvie-pro/forms/:id?"
                        element={<FormInfo appName={ROUTE_AUTH_APP_LIST.ABBVIEPRO} />}
                    />
                    <Route
                        path="/abbvie-pro/forms/create"
                        element={<CreateForm appName={ROUTE_AUTH_APP_LIST.ABBVIEPRO} />}
                    />

                    {/* Abbvie Group Specialties */}
                    <Route
                        path="/abbvie-pro/group-specialties"
                        element={<CountrySpecialtyList />}
                    />

                    <Route
                        path="/abbvie-pro/group-specialties/create"
                        element={<CreateGroupSpeciality />}
                    />

                    <Route
                        path="/abbvie-pro/group-specialties/:country?/create"
                        element={<CreateGroupSpecialityMap />}
                    />

                    <Route
                        path="/abbvie-pro/group-specialties/:country?"
                        element={<GroupSpecialtyList />}
                    />

                    {/* Veeva Data */}
                    <Route path="/abbvie-pro/veeva-data" element={<VeevaList />} />
                    <Route path="/abbvie-pro/veeva-data/create" element={<CreateVeeva />} />

                    {/* Persona */}
                    <Route path="/abbvie-pro/persona" element={<PersonaAppList />} />
                    <Route path="/abbvie-pro/persona/group" element={<PersonaGroup />} />
                    <Route
                        path="/abbvie-pro/persona/group/create"
                        element={<CreatePersonaGroup />}
                    />

                    <Route path="/abbvie-pro/persona/audience" element={<PersonaAudience />} />
                    <Route
                        path="/abbvie-pro/persona/audience/create"
                        element={<CreatePersonaAudience />}
                    />
                </>
            )}

            {(auth.superAdmin || (auth.siteAdmin && auth.allerganPro)) && (
                <>
                    <Route path="/allergan-pro" element={<AllerganProAppList />} />
                    <Route
                        path="/allergan-pro/forms"
                        element={<FormList appName={ROUTE_AUTH_APP_LIST.ALLERGANPRO} />}
                    />

                    <Route
                        path="/allergan-pro/forms/:id?"
                        element={<FormInfo appName={ROUTE_AUTH_APP_LIST.ALLERGANPRO} />}
                    />
                    <Route
                        path="/allergan-pro/forms/create"
                        element={<CreateForm appName={ROUTE_AUTH_APP_LIST.ALLERGANPRO} />}
                    />
                    <Route path="/allergan-pro/tier" element={<TierCountryList />} />

                    <Route path="/allergan-pro/tier/create" element={<CreateTier />} />

                    <Route path="/allergan-pro/tier/:country" element={<CountryTierList />} />
                </>
            )}

            {(auth.superAdmin || (auth.siteAdmin && auth.lmbc)) && (
                <>
                    <Route exact path="/abbvie-lmbc/derm-finder" element={<DermFinder />} />

                    <Route path="/abbvie-lmbc/derm-finder/create" element={<CreateDermFinder />} />
                </>
            )}

            {(auth.superAdmin || auth.userManager) && (
                <>
                    <Route
                        path={`${auth.superAdmin ? "/user-list" : ""}/basic-login/create`}
                        element={<CreateBasicLoginUser />}
                    />

                    <Route
                        path={`${auth.superAdmin ? "/user-list" : ""}/basic-login`}
                        element={<BasicLoginUserList />}
                    />

                    <Route
                        path={`${auth.superAdmin ? "/user-list" : ""}/site-admin`}
                        element={<AdminUserList userType={ADMIN_TYPES.SITEADMIN} />}
                    />

                    <Route
                        path={`${auth.superAdmin ? "/user-list" : ""}/site-admin/create`}
                        element={<CreateAdmin userType={ADMIN_TYPES.SITEADMIN} />}
                    />

                    <Route
                        path={`${auth.superAdmin ? "/user-list" : ""}/system-admin/create`}
                        element={<CreateAdmin userType={ADMIN_TYPES.SYSTEMADMIN} />}
                    />

                    <Route
                        path={`${auth.superAdmin ? "/user-list" : ""}/user-manager`}
                        element={<AdminUserList userType={ADMIN_TYPES.USERMANAGER} />}
                    />

                    <Route
                        path={`${auth.superAdmin ? "/user-list" : ""}/system-admin`}
                        element={<AdminUserList userType={ADMIN_TYPES.SYSTEMADMIN} />}
                    />
                </>
            )}

            <Route path="/sign-in" element={token ? <Navigate to="/" /> : <SignIn />} />
            {!loading && token && <Route path="*" element={<NotFound />} />}
            {!token && <Route path="*" element={<Navigate to="/sign-in" />} />}
        </Routes>
    );
};

export default MainRoutes;
